import {
	COMPATIBILITY_WILDCARD,
	OfferActionField,
	OfferConditionField,
	OfferField,
	OfferMappingField,
	OfferRowChange,
	OfferRuleField,
	OfferTermsBlockField,
	TermsConditionType,
} from "../constants";
import { diff, isEmpty } from "../../util/util";
import { isBlank } from "./offerDisplay";
import { getMappingIds, isApplyAll } from "./offerMapping";

/** Date fields compared by timestamp instead of string equality. */
const DATE_DIFF_FIELDS = new Set([OfferField.START_DATE, OfferField.END_DATE]);

const BASIC_DIFF_FIELDS = [
	OfferField.TITLE,
	OfferField.OFFER_CODE,
	OfferField.DESCRIPTION,
	OfferField.OFFER_TYPE,
	OfferField.SUB_OFFER_TYPE,
	OfferField.OFFER_GROUP_ID,
	OfferField.PRIORITY,
	OfferField.CHANNEL,
	OfferField.START_DATE,
	OfferField.END_DATE,
	OfferField.MAX_USAGE_COUNT,
	OfferField.INDIVIDUAL_MAX_USAGE_COUNT,
];

const ACTION_DIFF_FIELDS = [
	OfferActionField.TYPE,
	OfferActionField.VALUE,
	OfferActionField.TARGET,
	OfferActionField.MAX_AMOUNT_LIMIT,
	OfferActionField.MIN_AMOUNT_LIMIT,
];

/** Normalizes a non-date scalar for equality checks. */
const normalizeScalar = (value) => {
	if (isBlank(value)) return "";
	if (typeof value === "number") {
		return Number.isFinite(value) ? String(value) : "";
	}
	if (typeof value === "boolean") {
		return value ? "true" : "false";
	}
	return String(value).trim();
};

/** Normalizes a date value to a timestamp, or "" when missing/invalid. */
const normalizeDate = (value) => {
	if (isBlank(value)) return "";
	const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
	return Number.isNaN(time) ? "" : time;
};

/** Compares two field values, using timestamps for start/end dates. */
const valuesEqual = (a, b, field) => {
	if (DATE_DIFF_FIELDS.has(field)) {
		return normalizeDate(a) === normalizeDate(b);
	}
	return normalizeScalar(a) === normalizeScalar(b);
};

/** Canonical string for comparing mapping lists, including apply-all. */
const normalizeMappingForCompare = (value) => {
	if (isApplyAll(value)) return COMPATIBILITY_WILDCARD;
	return getMappingIds(value)
		.map((id) => String(id).trim())
		.filter(Boolean)
		.sort()
		.join(",");
};

/** Canonical shape for comparing one condition row. */
const normalizeConditionForCompare = (condition = {}) => ({
	subject: normalizeScalar(condition[OfferConditionField.SUBJECT]),
	operator: normalizeScalar(condition[OfferConditionField.OPERATOR]),
	value: normalizeScalar(condition[OfferConditionField.VALUE]),
});

/** True when a condition row has no subject, operator, or value. */
const isEmptyCondition = (condition) => {
	const normalized = normalizeConditionForCompare(condition);
	return !normalized.subject && !normalized.operator && normalized.value === "";
};

/** Canonical shape for comparing one terms block. */
const normalizeTermsForCompare = (block = {}) => {
	const type = normalizeScalar(block[OfferTermsBlockField.TYPE]);
	if (type === TermsConditionType.ORDERED_LIST) {
		const content = Array.isArray(block[OfferTermsBlockField.CONTENT]) ? block[OfferTermsBlockField.CONTENT] : [];
		const normalizedContent = content.map((item) => String(item || "").trim()).filter(Boolean);
		return { type, content: normalizedContent.join("\n") };
	}
	return {
		type,
		content: String(block[OfferTermsBlockField.CONTENT] || "").trim(),
	};
};

/** True when a terms block has no type or content. */
const isEmptyTermsBlock = (block) => {
	const normalized = normalizeTermsForCompare(block);
	return !normalized.type && !normalized.content;
};

/** Diffs two arrays by index into added, updated, and removed row changes. */
const diffIndexedRows = (baselineRows = [], draftRows = [], { isEmptyRow, normalizeRow }) => {
	const changes = [];
	const maxLen = Math.max(baselineRows.length, draftRows.length);
	for (let index = 0; index < maxLen; index += 1) {
		const baselineRow = baselineRows[index];
		const draftRow = draftRows[index];
		const baselineEmpty = baselineRow == null || isEmptyRow(baselineRow);
		const draftEmpty = draftRow == null || isEmptyRow(draftRow);

		if (baselineEmpty && draftEmpty) continue;

		if (baselineEmpty && !draftEmpty) {
			changes.push({ index, change: OfferRowChange.ADDED, value: draftRow });
			continue;
		}

		if (!baselineEmpty && draftEmpty) {
			changes.push({
				index,
				change: draftRow == null ? OfferRowChange.REMOVED : OfferRowChange.UPDATED,
				value: draftRow == null ? baselineRow : draftRow,
				baselineValue: baselineRow,
			});
			continue;
		}

		const diffResult = diff(normalizeRow(baselineRow), normalizeRow(draftRow));

		if (!isEmpty(diffResult)) {
			changes.push({
				index,
				change: OfferRowChange.UPDATED,
				value: draftRow,
				baselineValue: baselineRow,
			});
		}
	}
	return changes;
};

/**
 * Diffs an edit draft against the baseline captured when Edit opened.
 * Returns only changed fields/rows for overview display. Save still uses the full draft.
 */
export const diffOfferDraft = (baseline, draft) => {
	if (!baseline || !draft) {
		return {
			isDirty: false,
			basic: {},
			rules: { fields: {}, conditions: [] },
			action: {},
			mapping: {},
			terms: [],
		};
	}

	const basic = {};
	BASIC_DIFF_FIELDS.forEach((field) => {
		if (!valuesEqual(baseline[field], draft[field], field)) {
			basic[field] = draft[field];
		}
	});

	const baselineRule = baseline[OfferField.RULE] || {};
	const draftRule = draft[OfferField.RULE] || {};
	const rulesFields = {};
	if (!valuesEqual(baselineRule[OfferRuleField.DESCRIPTION], draftRule[OfferRuleField.DESCRIPTION])) {
		rulesFields[OfferRuleField.DESCRIPTION] = draftRule[OfferRuleField.DESCRIPTION];
	}
	if (!valuesEqual(baselineRule[OfferRuleField.OPERATOR], draftRule[OfferRuleField.OPERATOR])) {
		rulesFields[OfferRuleField.OPERATOR] = draftRule[OfferRuleField.OPERATOR];
	}

	const conditions = diffIndexedRows(
		baselineRule[OfferRuleField.CONDITIONS] || [],
		draftRule[OfferRuleField.CONDITIONS] || [],
		{ isEmptyRow: isEmptyCondition, normalizeRow: normalizeConditionForCompare }
	);

	const baselineAction = baselineRule[OfferRuleField.ACTION] || {};
	const draftAction = draftRule[OfferRuleField.ACTION] || {};
	const action = {};
	ACTION_DIFF_FIELDS.forEach((field) => {
		if (!valuesEqual(baselineAction[field], draftAction[field])) {
			action[field] = draftAction[field];
		}
	});

	const baselineMapping = baseline[OfferField.OFFER_MAPPING] || {};
	const draftMapping = draft[OfferField.OFFER_MAPPING] || {};
	const mapping = {};

	const mappingFields = [
		OfferMappingField.MENU_IDS,
		OfferMappingField.RESTAURANT_IDS,
		OfferMappingField.USER_IDS,
	];

	for (const field of mappingFields) {
		if (
			normalizeMappingForCompare(baselineMapping[field]) !==
			normalizeMappingForCompare(draftMapping[field])
		) {
			mapping[field] = draftMapping[field];
		}
	}

	const terms = diffIndexedRows(
		baseline[OfferField.TERMS_CONDITION] || [],
		draft[OfferField.TERMS_CONDITION] || [],
		{ isEmptyRow: isEmptyTermsBlock, normalizeRow: normalizeTermsForCompare }
	);
	
	const changes = { basic, rulesFields, conditions, action, mapping, terms };
	const isDirty = Object.values(changes).some((section) => !isEmpty(section));

	return {
		isDirty,
		basic,
		rules: { fields: rulesFields, conditions },
		action,
		mapping,
		terms,
	};
};

/** True when the edit draft differs from the baseline in any compared field. */
export const isOfferDraftDirty = (baseline, draft) => diffOfferDraft(baseline, draft).isDirty;
